import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import { ProRace } from '@radevents/shared';
import api from '../services/api';
import { isRunning, isUpcoming } from '../utils/proRaceStatus';
import { BroadcasterChips, RaceGroup, nachMonat } from '../components/ProRaceZeile';
import PublicHeader from '../components/Layout/PublicHeader';
import AppTopbarBrand from '../components/Layout/AppTopbarBrand';

import { SITE_ORIGIN } from '../config/siteOrigin';
const CANONICAL = `${SITE_ORIGIN}/radsport-im-tv`;
const PAGE_TITLE = 'Radsport im TV 2026 – Tour de France, Giro & Vuelta live & im Stream';
const PAGE_DESC = 'Wo läuft der Radsport 2026 im TV? Alle Übertragungen und Livestreams der großen Profi-Rennen – Tour de France, Giro d’Italia, Vuelta a España & Co. auf ARD, ZDF, Eurosport und discovery+.';

// Die Sender-Chips stehen seit dem 30.10.2026 bei der Rennzeile (`components/ProRaceZeile`),
// die Kalender und diese Seite teilen.
export { BroadcasterChips };

function buildTvHubSchema(races: ProRace[]): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Radsport im TV 2026 – Übertragungen',
    description: PAGE_DESC,
    url: CANONICAL,
    itemListElement: races.map((race, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: `${race.name} – ${race.broadcastInfo ?? 'Übertragung'}`,
      url: `${SITE_ORIGIN}/rennkalender/${race.slug}`,
    })),
  };
}

/**
 * Radsport im TV (Neugestaltung 30.10.2026): derselbe Aufbau wie der Rennkalender, dessen
 * Unterseite sie ist. Kopf wie die Detailseiten, kurzer Titel, „Läuft gerade“ oben, dann nach
 * Monat, je Rennen die Sender als Chips. Die Fragen bleiben für Suchmaschinen, aber zugeklappt.
 */
export default function RadsportImTV() {
  const navigate = useNavigate();
  const zurueck = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/rennkalender');
  };
  const [races, setRaces] = useState<ProRace[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/pro-races')
      .then(r => setRaces(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upcoming = races
    .filter(r => isUpcoming(r.endDate))
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const liveNow = upcoming.filter(r => isRunning(r.startDate, r.endDate));
  const later = upcoming.filter(r => !isRunning(r.startDate, r.endDate));
  const withBroadcast = upcoming.filter(r => r.broadcastInfo);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
        {withBroadcast.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(buildTvHubSchema(withBroadcast))}</script>
        )}
      </Helmet>

      <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
        <div className="hide-mobile"><PublicHeader /></div>
        <div className="app-topbar hide-desktop"><AppTopbarBrand onBack={zurueck} /></div>

        <main className="rk-seite">
          <Link to="/rennkalender" className="pf-zurueck hide-mobile" style={{ marginBottom: 10, textDecoration: 'none' }}><ChevronLeft size={16} aria-hidden />Rennkalender</Link>
          <div className="rk-kopf">
            <div>
              <h1 className="rk-titel">Radsport im TV</h1>
              <p className="rk-unterzeile">Wo die Profirennen laufen, frei empfangbar und im Stream.</p>
            </div>
          </div>

          {loading ? (
            <div className="rk-liste" aria-busy style={{ marginTop: 22 }}>{[0, 1, 2].map(i => <div key={i} className="rk-platzhalter" />)}</div>
          ) : upcoming.length === 0 ? (
            <div className="rk-leer"><p>Gerade steht keine Übertragung an. Der Kalender für die nächste Saison folgt.</p></div>
          ) : (
            <>
              {liveNow.length > 0 && <RaceGroup label="Läuft gerade" races={liveNow} live mitSendern />}
              {nachMonat(later).map(g => <RaceGroup key={g.key} label={g.label} races={g.races} mitSendern />)}
            </>
          )}

          <section className="rk-fragen" aria-label="Häufige Fragen">
            <h2 className="rk-monat">Häufige Fragen</h2>
            <FaqItem q="Wo läuft die Tour de France im TV?">
              In Deutschland zeigt die <strong>ARD</strong> täglich live und in der ARD-Mediathek.{' '}
              <strong>Eurosport 1</strong> und <strong>discovery+</strong> übertragen jede Etappe in voller Länge.
              Alle Etappen stehen im <Link to="/rennkalender/tour-de-france-2026">Etappenplan</Link>.
            </FaqItem>
            <FaqItem q="Kann ich Radsport kostenlos im Livestream sehen?">
              Ja. Die Mediatheken von ARD und ZDF zeigen ihre Live-Übertragungen kostenlos. Alle Etappen in voller
              Länge gibt es bei Eurosport und discovery+.
            </FaqItem>
            <FaqItem q="Welche Radrennen laufen im Free-TV?">
              Vor allem die großen Rundfahrten, also Tour de France und teils Giro d’Italia und Vuelta a España,
              dazu deutsche Rennen wie die Cyclassics Hamburg und die Deutschland Tour.
            </FaqItem>
          </section>
        </main>
      </div>
    </>
  );
}

function FaqItem({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <details className="rk-frage">
      <summary>{q}<ChevronDown size={16} aria-hidden /></summary>
      <p>{children}</p>
    </details>
  );
}
